import type { UserMessageRailEntry } from "@t3tools/contracts";
import {
  type EnvironmentId,
  type MessageId,
  type ServerAgentStatus,
  type ScopedThreadRef,
  type ThreadId,
  type TurnId,
  ClientOrchestrationThreadActivity,
} from "@t3tools/contracts";
import { scopedThreadKey, scopeProjectRef, scopeThreadRef } from "@t3tools/client-runtime";
import { truncate } from "@t3tools/shared/String";
import { TimelineScrollToBottomPill, useTimelineAutoScroll } from "./chat/timelineAutoScroll";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { readEnvironmentApi } from "../environmentApi";
import { isUploadAbortedError, uploadComposerAttachment } from "../attachmentUpload";
import { compressImageForUpload } from "./chat/imageCompression";
import { isElectron } from "../env";
import { collapseExpandedComposerCursor } from "../composer-logic";
import {
  derivePhase,
  deriveTimelineEntries,
  deriveActiveWorkStartedAt,
  deriveActivePlanState,
  deriveWorkLogEntries,
  isLatestTurnSettled,
} from "../session-logic";
import { type TimelineScrollHandle } from "./chat/useTimelineScroll";
import { selectEnvironmentState, useStore } from "../store";
import { createProjectSelectorByRef, createThreadSelectorByRef } from "../storeSelectors";
import { useUiStateStore } from "../uiStateStore";
import { type ChatMessage, type SessionPhase, type Thread } from "../types";
import { useTheme } from "../hooks/useTheme";
import { useCommandPaletteStore } from "../commandPaletteStore";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { RIGHT_PANEL_INLINE_LAYOUT_MEDIA_QUERY } from "../rightPanelLayout";
import PlanSidebar from "./PlanSidebar";
import { cn } from "~/lib/utils";
import { newCommandId, newMessageId } from "~/lib/utils";
import { leaseServerObjectUrl } from "~/lib/serverObjectUrl";
import { useSettings } from "../hooks/useSettings";
import { useDraftProjectSelection } from "../hooks/useDraftProjectSelection";
import {
  type ComposerFileAttachment,
  type ComposerImageAttachment,
  useComposerDraftStore,
  type DraftId,
} from "../composerDraftStore";
import { ChatComposer, type ChatComposerHandle } from "./chat/ChatComposer";
import { ExpandedImageDialog } from "./chat/ExpandedImageDialog";
import { MessagesTimeline } from "./chat/MessagesTimeline";
import { PromptExamples } from "./chat/PromptExamples";
import { useRailMessageSelection } from "./chat/useRailMessageSelection";
import { UserMessageRail } from "./chat/UserMessageRail";
import { isUserMessageRailVisible } from "./chat/userMessageRail.logic";
import { ChatHeader } from "./chat/ChatHeader";
import { type ExpandedImagePreview } from "./chat/ExpandedImagePreview";
import { NoActiveThreadState } from "./NoActiveThreadState";
import { failureMessageKey } from "./chat/inferenceFailureMessages";
import {
  buildLocalDraftThread,
  collectUserMessageBlobPreviewUrls,
  createLocalDispatchSnapshot,
  deriveComposerSendState,
  formatUploadProgressLabel,
  hasServerAcknowledgedLocalDispatch,
  type LocalDispatchSnapshot,
  type LocalDraftThread,
  type UploadProgressSnapshot,
  cloneComposerImageForRetry,
  revokeBlobPreviewUrl,
  revokeUserMessagePreviewUrls,
} from "./ChatView.logic";
import { useComposerHandleContext } from "../composerHandleContext";
import { useServerConfig, useServerKeybindings } from "~/rpc/serverState";
import { isTransportConnectionErrorMessage } from "~/rpc/transportError";
import {
  loadOlderThreadMessages,
  loadOlderThreadActivities,
  retainThreadDetailSubscription,
} from "../environments/runtime/service";
import { RightPanelSheet } from "./RightPanelSheet";
import { useTranslation } from "react-i18next";
import { ensureInferenceCredentialForTurn } from "../environments/primary/inferenceCredential";
import { useServerAuthPolicy } from "../environments/primary/auth";

const IMAGE_ONLY_BOOTSTRAP_PROMPT =
  "[User attached one or more images without additional text. Respond using the conversation context and the attached image(s).]";
const ATTACHMENT_ONLY_BOOTSTRAP_PROMPT =
  "[User attached one or more files without additional text.]";
const EMPTY_ACTIVITIES: ClientOrchestrationThreadActivity[] = [];
const EMPTY_AGENT_SKILLS: ServerAgentStatus["skills"] = [];
const TYPE_TO_FOCUS_EDITABLE_SELECTOR = [
  "input",
  "textarea",
  "select",
  '[contenteditable="true"]',
  '[contenteditable="plaintext-only"]',
  '[role="textbox"]',
].join(",");
const TYPE_TO_FOCUS_INTERACTIVE_SELECTOR = [
  "button",
  "a[href]",
  "summary",
  '[role="button"]',
  '[role="checkbox"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
].join(",");
const TYPE_TO_FOCUS_FLOATING_LAYER_SELECTOR = [
  '[data-slot="dialog"]',
  '[data-slot="menu-popup"]',
  '[data-slot="select-popup"]',
  '[data-slot="popover-popup"]',
  '[data-slot="combobox-popup"]',
  '[data-slot="autocomplete-popup"]',
].join(",");

function eventTargetElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
}

function shouldTypeToFocusComposer(event: KeyboardEvent): boolean {
  if (event.defaultPrevented || event.isComposing) return false;
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  if (event.key.length !== 1) return false;

  const target = eventTargetElement(event.target);
  if (target?.closest(TYPE_TO_FOCUS_EDITABLE_SELECTOR)) return false;
  if (target?.closest(TYPE_TO_FOCUS_INTERACTIVE_SELECTOR)) return false;
  if (document.querySelector(TYPE_TO_FOCUS_FLOATING_LAYER_SELECTOR)) return false;

  return true;
}

type ChatViewProps =
  | {
      environmentId: EnvironmentId;
      threadId: ThreadId;
      reserveTitleBarControlInset?: boolean;
      routeKind: "server";
      draftId?: never;
    }
  | {
      environmentId: EnvironmentId;
      threadId: ThreadId;
      reserveTitleBarControlInset?: boolean;
      routeKind: "draft";
      draftId: DraftId;
    };

function useLocalDispatchState(input: {
  activeThread: Thread | LocalDraftThread | undefined;
  activeLatestTurn: Thread["latestTurn"] | null;
  phase: SessionPhase;
  activities: Thread["activities"];
}) {
  const [localDispatch, setLocalDispatch] = useState<LocalDispatchSnapshot | null>(null);

  const beginLocalDispatch = useCallback(
    (options?: { uploading?: UploadProgressSnapshot | null }) => {
      setLocalDispatch((current) => {
        if (current) {
          const uploading =
            options && "uploading" in options ? (options.uploading ?? null) : current.uploading;
          return current.uploading === uploading ? current : { ...current, uploading };
        }
        return createLocalDispatchSnapshot(input.activeThread, options);
      });
    },
    [input.activeThread],
  );

  const resetLocalDispatch = useCallback(() => {
    setLocalDispatch(null);
  }, []);

  const serverAcknowledgedLocalDispatch = useMemo(
    () =>
      hasServerAcknowledgedLocalDispatch({
        localDispatch,
        phase: input.phase,
        latestTurn: input.activeLatestTurn,
        session: input.activeThread?.session ?? null,
        activities: input.activities,
      }),
    [
      input.activeLatestTurn,
      input.activeThread?.session,
      input.phase,
      input.activities,
      localDispatch,
    ],
  );

  useEffect(() => {
    if (!serverAcknowledgedLocalDispatch) {
      return;
    }
    resetLocalDispatch();
  }, [resetLocalDispatch, serverAcknowledgedLocalDispatch]);

  return {
    beginLocalDispatch,
    resetLocalDispatch,
    localDispatchStartedAt: localDispatch?.startedAt ?? null,
    uploadProgress: localDispatch?.uploading ?? null,
    isSendBusy: localDispatch !== null && !serverAcknowledgedLocalDispatch,
  };
}

const EMPTY_RAIL_ENTRIES: ReadonlyArray<UserMessageRailEntry> = [];

export default function ChatView(props: ChatViewProps) {
  const { t, i18n } = useTranslation();
  const { environmentId, threadId, routeKind, reserveTitleBarControlInset = true } = props;
  const draftId = routeKind === "draft" ? props.draftId : null;
  const routeThreadRef = useMemo(
    () => scopeThreadRef(environmentId, threadId),
    [environmentId, threadId],
  );
  const routeThreadKey = useMemo(() => scopedThreadKey(routeThreadRef), [routeThreadRef]);
  const composerDraftTarget: ScopedThreadRef | DraftId =
    routeKind === "server" ? routeThreadRef : props.draftId;
  const currentComposerDraftTarget = useRef(composerDraftTarget);
  currentComposerDraftTarget.current = composerDraftTarget;
  const serverThread = useStore(
    useMemo(
      () => createThreadSelectorByRef(routeKind === "server" ? routeThreadRef : null),
      [routeKind, routeThreadRef],
    ),
  );
  const markThreadVisited = useUiStateStore((store) => store.markThreadVisited);
  const activeThreadLastVisitedAt = useUiStateStore((store) =>
    routeKind === "server" ? store.threadLastVisitedAtById[routeThreadKey] : undefined,
  );
  const settings = useSettings();
  const timestampFormat = settings.timestampFormat;
  const autoOpenPlanSidebar = settings.autoOpenPlanSidebar;
  const { resolvedTheme } = useTheme();
  // Granular store selectors — avoid subscribing to prompt changes.
  const setComposerDraftPrompt = useComposerDraftStore((store) => store.setPrompt);
  const addComposerDraftImages = useComposerDraftStore((store) => store.addImages);
  const addComposerDraftFileAttachments = useComposerDraftStore(
    (store) => store.addFileAttachments,
  );
  const clearComposerDraftContent = useComposerDraftStore((store) => store.clearComposerContent);
  const draftThread = useComposerDraftStore((store) =>
    routeKind === "server"
      ? store.getDraftSessionByRef(routeThreadRef)
      : draftId
        ? store.getDraftSession(draftId)
        : null,
  );
  const promptRef = useRef("");
  const composerImagesRef = useRef<ComposerImageAttachment[]>([]);
  const composerFileAttachmentsRef = useRef<ComposerFileAttachment[]>([]);
  const localComposerRef = useRef<ChatComposerHandle | null>(null);
  const composerRef = useComposerHandleContext() ?? localComposerRef;
  const [expandedImage, setExpandedImage] = useState<ExpandedImagePreview | null>(null);
  const [optimisticUserMessages, setOptimisticUserMessages] = useState<ChatMessage[]>([]);
  const optimisticUserMessagesRef = useRef(optimisticUserMessages);
  optimisticUserMessagesRef.current = optimisticUserMessages;
  const [composerErrors, setComposerErrors] = useState<Record<string, string | null>>({});
  const [isConnecting, _setIsConnecting] = useState(false);
  const [isAcquiringCredential, setIsAcquiringCredential] = useState(false);
  const [planSidebarOpen, setPlanSidebarOpen] = useState(false);
  const shouldUsePlanSidebarSheet = useMediaQuery(RIGHT_PANEL_INLINE_LAYOUT_MEDIA_QUERY);
  // Tracks whether the user explicitly dismissed the sidebar for the active turn.
  const planSidebarDismissedForTurnRef = useRef<string | null>(null);
  const [attachmentPreviewHandoffByMessageId, setAttachmentPreviewHandoffByMessageId] = useState<
    Record<string, string[]>
  >({});
  const timelineRef = useRef<TimelineScrollHandle | null>(null);
  const { isAtEndRef, showScrollToBottom, onIsAtEndChange, scrollToEnd, resetToEnd } =
    useTimelineAutoScroll(timelineRef);
  const attachmentPreviewHandoffByMessageIdRef = useRef<Record<string, string[]>>({});
  const attachmentPreviewPromotionInFlightByMessageIdRef = useRef<Record<string, true>>({});
  const sendInFlightRef = useRef(false);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);

  const localDraftThread = useMemo(
    () => (draftThread ? buildLocalDraftThread(threadId, draftThread) : undefined),
    [draftThread, threadId],
  );
  const isServerThread = routeKind === "server" && serverThread !== undefined;
  const activeThread = isServerThread ? serverThread : localDraftThread;
  const activeThreadMessageWindow = useStore((storeState) =>
    activeThread
      ? selectEnvironmentState(storeState, activeThread.environmentId).messageWindowByThreadId[
          activeThread.id
        ]
      : undefined,
  );
  const isThreadDetailLoading = isServerThread && activeThreadMessageWindow === undefined;
  const messagesWrapperRef = useRef<HTMLDivElement | null>(null);
  const onJumpToMessage = useRailMessageSelection({
    environmentId,
    threadId,
    messages: activeThread?.messages,
    window: activeThreadMessageWindow,
    timelineRef,
  });
  const handleLoadOlderMessages = useCallback(() => {
    if (activeThread) {
      loadOlderThreadMessages(activeThread.environmentId, activeThread.id);
      loadOlderThreadActivities(activeThread.environmentId, activeThread.id);
    }
  }, [activeThread]);
  const isLocalDraftThread = !isServerThread && localDraftThread !== undefined;
  const activeThreadId = activeThread?.id ?? null;
  const activeLatestTurn = activeThread?.latestTurn ?? null;
  const latestTurnSettled = isLatestTurnSettled(activeLatestTurn, activeThread?.session ?? null);
  const activeProjectRef = activeThread?.projectId
    ? scopeProjectRef(activeThread.environmentId, activeThread.projectId)
    : null;
  const activeProject = useStore(
    useMemo(() => createProjectSelectorByRef(activeProjectRef), [activeProjectRef]),
  );
  const isTeamServer = useServerAuthPolicy((state) => state.policy === "team-server");
  const activeProjectOwner = isTeamServer ? (activeProject?.createdBy ?? null) : null;
  const { draftProjectPicker, openProjectPicker } = useDraftProjectSelection({
    draftId,
    draftSession: routeKind === "draft" ? draftThread : null,
    activeProject,
  });

  useEffect(() => {
    if (routeKind !== "server") {
      return;
    }
    return retainThreadDetailSubscription(environmentId, threadId);
  }, [environmentId, routeKind, threadId]);

  const activeEnvironmentUnavailableState = null;
  useEffect(() => {
    if (!serverThread?.id) return;
    if (!latestTurnSettled) return;
    if (!activeLatestTurn?.completedAt) return;
    const turnCompletedAt = Date.parse(activeLatestTurn.completedAt);
    if (Number.isNaN(turnCompletedAt)) return;
    const lastVisitedAt = activeThreadLastVisitedAt ? Date.parse(activeThreadLastVisitedAt) : NaN;
    if (!Number.isNaN(lastVisitedAt) && lastVisitedAt >= turnCompletedAt) return;

    markThreadVisited(
      scopedThreadKey(scopeThreadRef(serverThread.environmentId, serverThread.id)),
      activeLatestTurn.completedAt,
    );
  }, [
    activeLatestTurn?.completedAt,
    activeThreadLastVisitedAt,
    latestTurnSettled,
    markThreadVisited,
    serverThread?.environmentId,
    serverThread?.id,
  ]);

  const serverConfig = useServerConfig();
  const agentStatus = serverConfig?.agent ?? null;
  const phase = derivePhase(activeThread?.session ?? null);
  const threadActivities = activeThread?.activities ?? EMPTY_ACTIVITIES;
  const workLogEntries = useMemo(
    () =>
      deriveWorkLogEntries(
        threadActivities,
        activeLatestTurn?.turnId ?? undefined,
        latestTurnSettled,
        i18n.language,
      ),
    [activeLatestTurn?.turnId, i18n.language, latestTurnSettled, threadActivities],
  );
  const activePlan = useMemo(
    () => deriveActivePlanState(threadActivities, activeLatestTurn?.turnId ?? undefined),
    [activeLatestTurn?.turnId, threadActivities],
  );
  const {
    beginLocalDispatch,
    resetLocalDispatch,
    localDispatchStartedAt,
    uploadProgress,
    isSendBusy,
  } = useLocalDispatchState({
    activeThread,
    activeLatestTurn,
    phase,
    activities: threadActivities,
  });
  const isWorking = phase === "running" || isSendBusy || isConnecting;
  const activeWorkStartedAt = deriveActiveWorkStartedAt(
    activeLatestTurn,
    activeThread?.session ?? null,
    localDispatchStartedAt,
  );
  useEffect(() => {
    attachmentPreviewHandoffByMessageIdRef.current = attachmentPreviewHandoffByMessageId;
  }, [attachmentPreviewHandoffByMessageId]);
  const clearAttachmentPreviewHandoff = useCallback(
    (messageId: MessageId, previewUrls?: ReadonlyArray<string>) => {
      delete attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId];
      const currentPreviewUrls =
        previewUrls ?? attachmentPreviewHandoffByMessageIdRef.current[messageId] ?? [];
      setAttachmentPreviewHandoffByMessageId((existing) => {
        if (!(messageId in existing)) {
          return existing;
        }
        const next = { ...existing };
        delete next[messageId];
        attachmentPreviewHandoffByMessageIdRef.current = next;
        return next;
      });
      for (const previewUrl of currentPreviewUrls) {
        revokeBlobPreviewUrl(previewUrl);
      }
    },
    [],
  );
  const clearAttachmentPreviewHandoffs = useCallback(() => {
    attachmentPreviewPromotionInFlightByMessageIdRef.current = {};
    for (const previewUrls of Object.values(attachmentPreviewHandoffByMessageIdRef.current)) {
      for (const previewUrl of previewUrls) {
        revokeBlobPreviewUrl(previewUrl);
      }
    }
    attachmentPreviewHandoffByMessageIdRef.current = {};
    setAttachmentPreviewHandoffByMessageId({});
  }, []);
  useEffect(() => {
    return () => {
      clearAttachmentPreviewHandoffs();
      for (const message of optimisticUserMessagesRef.current) {
        revokeUserMessagePreviewUrls(message);
      }
    };
  }, [clearAttachmentPreviewHandoffs]);
  const handoffAttachmentPreviews = useCallback((messageId: MessageId, previewUrls: string[]) => {
    if (previewUrls.length === 0) return;

    const previousPreviewUrls = attachmentPreviewHandoffByMessageIdRef.current[messageId] ?? [];
    const nextPreviewUrlSet = new Set(previewUrls);
    for (const previewUrl of previousPreviewUrls) {
      if (!nextPreviewUrlSet.has(previewUrl)) {
        revokeBlobPreviewUrl(previewUrl);
      }
    }
    setAttachmentPreviewHandoffByMessageId((existing) => {
      const next = {
        ...existing,
        [messageId]: previewUrls,
      };
      attachmentPreviewHandoffByMessageIdRef.current = next;
      return next;
    });
  }, []);
  const serverMessages = activeThread?.messages;
  useEffect(() => {
    if (typeof Image === "undefined" || !serverMessages || serverMessages.length === 0) {
      return;
    }

    const cleanups: Array<() => void> = [];

    for (const [messageId, handoffPreviewUrls] of Object.entries(
      attachmentPreviewHandoffByMessageId,
    )) {
      if (attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId]) {
        continue;
      }

      const serverMessage = serverMessages.find(
        (message) => message.id === messageId && message.role === "user",
      );
      if (!serverMessage?.attachments || serverMessage.attachments.length === 0) {
        continue;
      }

      const serverPreviewUrls = serverMessage.attachments.flatMap((attachment) =>
        attachment.type === "image" && attachment.previewUrl ? [attachment.previewUrl] : [],
      );
      if (
        serverPreviewUrls.length === 0 ||
        serverPreviewUrls.length !== handoffPreviewUrls.length ||
        serverPreviewUrls.some((previewUrl) => previewUrl.startsWith("blob:"))
      ) {
        continue;
      }

      attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId] = true;

      let cancelled = false;
      // Loaded with the server token, as the tiles load them: the leases keep
      // the bytes for the tiles that take over from the local previews.
      const leases = serverPreviewUrls.map((previewUrl) => leaseServerObjectUrl(previewUrl));

      const preloadServerPreviews = Promise.all(
        leases.map(async (lease) => {
          if ((await lease.load) === null) {
            throw new Error(`Failed to load server preview for ${messageId}.`);
          }
        }),
      );

      void preloadServerPreviews
        .finally(() => {
          for (const lease of leases) lease.release();
        })
        .then(() => {
          if (cancelled) {
            return;
          }
          clearAttachmentPreviewHandoff(messageId as MessageId, handoffPreviewUrls);
        })
        .catch(() => {
          if (!cancelled) {
            delete attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId];
          }
        });

      cleanups.push(() => {
        cancelled = true;
        delete attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId];
        for (const lease of leases) lease.release();
      });
    }

    return () => {
      for (const cleanup of cleanups) {
        cleanup();
      }
    };
  }, [attachmentPreviewHandoffByMessageId, clearAttachmentPreviewHandoff, serverMessages]);
  const timelineMessages = useMemo(() => {
    const messages = serverMessages ?? [];
    const serverMessagesWithPreviewHandoff =
      Object.keys(attachmentPreviewHandoffByMessageId).length === 0
        ? messages
        : // Spread only fires for the few messages that actually changed;
          // unchanged ones early-return their original reference.
          // In-place mutation would break React's immutable state contract.
          // oxlint-disable-next-line no-map-spread
          messages.map((message) => {
            if (
              message.role !== "user" ||
              !message.attachments ||
              message.attachments.length === 0
            ) {
              return message;
            }
            const handoffPreviewUrls = attachmentPreviewHandoffByMessageId[message.id];
            if (!handoffPreviewUrls || handoffPreviewUrls.length === 0) {
              return message;
            }

            let changed = false;
            let imageIndex = 0;
            const attachments = message.attachments.map((attachment) => {
              if (attachment.type !== "image") {
                return attachment;
              }
              const handoffPreviewUrl = handoffPreviewUrls[imageIndex];
              imageIndex += 1;
              if (!handoffPreviewUrl || attachment.previewUrl === handoffPreviewUrl) {
                return attachment;
              }
              changed = true;
              return {
                ...attachment,
                previewUrl: handoffPreviewUrl,
              };
            });

            return changed ? { ...message, attachments } : message;
          });

    if (optimisticUserMessages.length === 0) {
      return serverMessagesWithPreviewHandoff;
    }
    const serverIds = new Set(serverMessagesWithPreviewHandoff.map((message) => message.id));
    const pendingMessages = optimisticUserMessages.filter((message) => !serverIds.has(message.id));
    if (pendingMessages.length === 0) {
      return serverMessagesWithPreviewHandoff;
    }
    return [...serverMessagesWithPreviewHandoff, ...pendingMessages];
  }, [serverMessages, attachmentPreviewHandoffByMessageId, optimisticUserMessages]);
  const timelineEntries = useMemo(
    () => deriveTimelineEntries(timelineMessages, workLogEntries, threadActivities),
    [timelineMessages, workLogEntries, threadActivities],
  );
  const railEntries =
    useStore((storeState) =>
      activeThread
        ? selectEnvironmentState(storeState, activeThread.environmentId).userMessageRailByThreadId[
            activeThread.id
          ]
        : undefined,
    ) ?? EMPTY_RAIL_ENTRIES;
  const railVisible = isServerThread && isUserMessageRailVisible(railEntries);
  const projectCwd = activeProject?.cwd ?? null;
  const keybindings = useServerKeybindings();
  const activeProjectCwd = activeProject?.cwd ?? null;
  const activeWorkspaceRoot = activeProjectCwd ?? undefined;
  const setComposerError = useCallback(
    (targetThreadId: ThreadId | null, message: string | null) => {
      if (!targetThreadId) return;
      const key = `${environmentId}:${targetThreadId}`;
      setComposerErrors((errors) =>
        errors[key] === message ? errors : { ...errors, [key]: message },
      );
    },
    [environmentId],
  );

  const focusComposer = useCallback(() => {
    composerRef.current?.focusAtEnd();
  }, [composerRef]);
  const onPickPromptExample = useCallback(
    (text: string) => {
      composerRef.current?.replacePrompt(text);
    },
    [composerRef],
  );
  const togglePlanSidebar = useCallback(() => {
    setPlanSidebarOpen((open) => {
      if (open) {
        planSidebarDismissedForTurnRef.current = activePlan?.turnId ?? "__dismissed__";
      } else {
        planSidebarDismissedForTurnRef.current = null;
      }
      return !open;
    });
  }, [activePlan?.turnId]);
  const closePlanSidebar = useCallback(() => {
    setPlanSidebarOpen(false);
    planSidebarDismissedForTurnRef.current = activePlan?.turnId ?? "__dismissed__";
  }, [activePlan?.turnId]);

  useEffect(() => {
    resetToEnd();
    setPlanSidebarOpen(false);
    planSidebarDismissedForTurnRef.current = null;
  }, [activeThread?.id, resetToEnd]);

  // Auto-open the plan sidebar when plan/todo steps arrive for the current turn.
  // Don't auto-open for plans carried over from a previous turn (the user can open manually).
  useEffect(() => {
    if (!autoOpenPlanSidebar) return;
    if (!activePlan) return;
    if (planSidebarOpen) return;
    const latestTurnId = activeLatestTurn?.turnId ?? null;
    if (latestTurnId && activePlan.turnId !== latestTurnId) return;
    const turnKey = activePlan.turnId ?? "__dismissed__";
    if (planSidebarDismissedForTurnRef.current === turnKey) return;
    setPlanSidebarOpen(true);
  }, [activePlan, activeLatestTurn?.turnId, autoOpenPlanSidebar, planSidebarOpen]);

  useEffect(() => {
    if (!activeThread?.id) return;
    const frame = window.requestAnimationFrame(() => {
      focusComposer();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [activeThread?.id, focusComposer]);

  useEffect(() => {
    if (!activeThread?.id) return;
    if (activeThread.messages.length === 0) {
      return;
    }
    const serverIds = new Set(activeThread.messages.map((message) => message.id));
    const removedMessages = optimisticUserMessages.filter((message) => serverIds.has(message.id));
    if (removedMessages.length === 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      setOptimisticUserMessages((existing) =>
        existing.filter((message) => !serverIds.has(message.id)),
      );
    }, 0);
    for (const removedMessage of removedMessages) {
      const previewUrls = collectUserMessageBlobPreviewUrls(removedMessage);
      if (previewUrls.length > 0) {
        handoffAttachmentPreviews(removedMessage.id, previewUrls);
        continue;
      }
      revokeUserMessagePreviewUrls(removedMessage);
    }
    return () => {
      window.clearTimeout(timer);
    };
  }, [activeThread?.id, activeThread?.messages, handoffAttachmentPreviews, optimisticUserMessages]);

  useEffect(() => {
    setOptimisticUserMessages((existing) => {
      for (const message of existing) {
        revokeUserMessagePreviewUrls(message);
      }
      return [];
    });
    resetLocalDispatch();
    setExpandedImage(null);
  }, [draftId, resetLocalDispatch, threadId]);

  const closeExpandedImage = useCallback(() => {
    setExpandedImage(null);
  }, []);

  useEffect(() => {
    const handler = (event: globalThis.KeyboardEvent) => {
      if (!activeThreadId || useCommandPaletteStore.getState().open || event.defaultPrevented) {
        return;
      }
      if (!shouldTypeToFocusComposer(event)) return;
      if (composerRef.current?.insertTextAtEnd(event.key)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [activeThreadId, composerRef]);

  const onSend = async (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    const api = readEnvironmentApi(environmentId);
    if (!api || !activeThread || isSendBusy || isConnecting || sendInFlightRef.current) return;
    setComposerError(activeThread.id, null);
    sendInFlightRef.current = true;
    setIsAcquiringCredential(true);
    const credential = await ensureInferenceCredentialForTurn();
    setIsAcquiringCredential(false);
    sendInFlightRef.current = false;
    if (currentComposerDraftTarget.current !== composerDraftTarget) return;
    if (credential.status !== "held") {
      setComposerError(
        activeThread.id,
        t(failureMessageKey("credential_missing", credential.status)),
      );
      return;
    }
    const sendCtx = composerRef.current?.getSendContext();
    if (!sendCtx) return;
    const { images: composerImages, files: composerFileAttachments, deepWork } = sendCtx;
    const promptForSend = promptRef.current;
    const { trimmedPrompt: trimmed, hasSendableContent } = deriveComposerSendState({
      prompt: promptForSend,
      imageCount: composerImages.length + composerFileAttachments.length,
    });
    if (!hasSendableContent) {
      return;
    }
    if (!activeProject) {
      if (draftProjectPicker) {
        openProjectPicker();
      }
      return;
    }
    const threadIdForSend = activeThread.id;
    const isFirstMessage = !isServerThread || activeThread.messages.length === 0;

    sendInFlightRef.current = true;

    const composerImagesSnapshot = [...composerImages];
    const composerFileAttachmentsSnapshot = [...composerFileAttachments];
    const uploadTasks = [
      ...composerImagesSnapshot.map((image) => ({ type: "image" as const, file: image.file })),
      ...composerFileAttachmentsSnapshot.map((attachment) => ({
        type: "file" as const,
        file: attachment.file,
      })),
    ];
    let uploadTotalBytes = uploadTasks.reduce((sum, task) => sum + task.file.size, 0);
    const uploadAbortController = uploadTasks.length > 0 ? new AbortController() : null;
    uploadAbortControllerRef.current = uploadAbortController;
    const uploadLoadedByIndex = uploadTasks.map(() => 0);
    let uploadDoneFiles = 0;
    let lastUploadReportKey = "";
    const reportUploadProgress = () => {
      const sentBytes = uploadLoadedByIndex.reduce((sum, loaded) => sum + loaded, 0);
      const percent = uploadTotalBytes > 0 ? Math.floor((sentBytes / uploadTotalBytes) * 100) : 0;
      const reportKey = `${percent}:${uploadDoneFiles}`;
      if (reportKey === lastUploadReportKey) return;
      lastUploadReportKey = reportKey;
      beginLocalDispatch({
        uploading: {
          sentBytes,
          totalBytes: uploadTotalBytes,
          doneFiles: uploadDoneFiles,
          totalFiles: uploadTasks.length,
        },
      });
    };

    beginLocalDispatch({
      uploading:
        uploadTasks.length > 0
          ? {
              sentBytes: 0,
              totalBytes: uploadTotalBytes,
              doneFiles: 0,
              totalFiles: uploadTasks.length,
            }
          : null,
    });

    const messageIdForSend = newMessageId();
    const messageCreatedAt = new Date().toISOString();
    const outgoingMessageText =
      trimmed ||
      (composerImagesSnapshot.length > 0
        ? IMAGE_ONLY_BOOTSTRAP_PROMPT
        : ATTACHMENT_ONLY_BOOTSTRAP_PROMPT);
    // Sequential on purpose: parallel uploads share the same uplink without
    // finishing sooner, and the K/N progress label tracks file order.
    const turnAttachmentsPromise = (async () => {
      const uploaded = [];
      for (const [index, task] of uploadTasks.entries()) {
        const file = task.type === "image" ? await compressImageForUpload(task.file) : task.file;
        uploadTotalBytes -= task.file.size - file.size;
        uploaded.push(
          await uploadComposerAttachment({
            environmentId,
            threadId: threadIdForSend,
            projectId: activeProject.id,
            type: task.type,
            file,
            ...(uploadAbortController ? { signal: uploadAbortController.signal } : {}),
            onProgress: (loadedBytes) => {
              uploadLoadedByIndex[index] = loadedBytes;
              reportUploadProgress();
            },
          }),
        );
        uploadLoadedByIndex[index] = file.size;
        uploadDoneFiles += 1;
        reportUploadProgress();
      }
      return uploaded;
    })();
    const optimisticAttachments = [
      ...composerImagesSnapshot.map((image) => ({
        type: "image" as const,
        id: image.id,
        name: image.name,
        mimeType: image.mimeType,
        sizeBytes: image.sizeBytes,
        previewUrl: image.previewUrl,
      })),
      ...composerFileAttachmentsSnapshot.map((attachment) => ({
        type: "file" as const,
        id: attachment.id,
        name: attachment.name,
        mimeType: attachment.mimeType,
        sizeBytes: attachment.sizeBytes,
      })),
    ];
    setOptimisticUserMessages((existing) => [
      ...existing,
      {
        id: messageIdForSend,
        role: "user",
        text: outgoingMessageText,
        ...(optimisticAttachments.length > 0 ? { attachments: optimisticAttachments } : {}),
        createdAt: messageCreatedAt,
        streaming: false,
      },
    ]);
    scrollToEnd();

    setComposerError(threadIdForSend, null);
    promptRef.current = "";
    clearComposerDraftContent(composerDraftTarget);
    composerRef.current?.resetCursorState();

    let turnStartSucceeded = false;
    await (async () => {
      let firstComposerImageName: string | null = null;
      if (composerImagesSnapshot.length > 0) {
        const firstComposerImage = composerImagesSnapshot[0];
        if (firstComposerImage) {
          firstComposerImageName = firstComposerImage.name;
        }
      }
      let firstComposerFileName: string | null = null;
      if (composerFileAttachmentsSnapshot.length > 0) {
        const firstComposerFile = composerFileAttachmentsSnapshot[0];
        if (firstComposerFile) {
          firstComposerFileName = firstComposerFile.name;
        }
      }
      let titleSeed = trimmed;
      if (!titleSeed) {
        if (firstComposerImageName) {
          titleSeed = t("common/image_a2u2y3", { firstComposerImageName });
        } else if (firstComposerFileName) {
          titleSeed = t("common/file_3fs6lu", { firstComposerFileName });
        } else {
          titleSeed = t("common/newThread_iazwum");
        }
      }
      const title = truncate(titleSeed);

      // Auto-title from first message
      if (isFirstMessage && isServerThread) {
        await api.orchestration.dispatchCommand({
          type: "thread.meta.update",
          commandId: newCommandId(),
          threadId: threadIdForSend,
          title,
        });
      }

      const turnAttachments = await turnAttachmentsPromise;
      uploadAbortControllerRef.current = null;
      const bootstrap = isLocalDraftThread
        ? { createThread: { projectId: activeProject.id, title } }
        : undefined;
      beginLocalDispatch({ uploading: null });
      await api.orchestration.dispatchCommand({
        type: "thread.turn.start",
        commandId: newCommandId(),
        threadId: threadIdForSend,
        message: {
          messageId: messageIdForSend,
          role: "user",
          text: outgoingMessageText,
          attachments: turnAttachments,
        },
        deepWork,
        titleSeed: title,
        ...(bootstrap ? { bootstrap } : {}),
      });
      turnStartSucceeded = true;
    })().catch(async (err: unknown) => {
      uploadAbortControllerRef.current = null;
      if (
        !turnStartSucceeded &&
        promptRef.current.length === 0 &&
        composerImagesRef.current.length === 0 &&
        composerFileAttachmentsRef.current.length === 0
      ) {
        setOptimisticUserMessages((existing) => {
          const removed = existing.filter((message) => message.id === messageIdForSend);
          for (const message of removed) {
            revokeUserMessagePreviewUrls(message);
          }
          const next = existing.filter((message) => message.id !== messageIdForSend);
          return next.length === existing.length ? existing : next;
        });
        promptRef.current = promptForSend;
        const retryComposerImages = composerImagesSnapshot.map(cloneComposerImageForRetry);
        composerImagesRef.current = retryComposerImages;
        composerFileAttachmentsRef.current = composerFileAttachmentsSnapshot;
        setComposerDraftPrompt(composerDraftTarget, promptForSend);
        addComposerDraftImages(composerDraftTarget, retryComposerImages);
        addComposerDraftFileAttachments(composerDraftTarget, composerFileAttachmentsSnapshot);
        composerRef.current?.resetCursorState({
          cursor: collapseExpandedComposerCursor(promptForSend, promptForSend.length),
          prompt: promptForSend,
          detectTrigger: true,
        });
      }
      if (
        !isUploadAbortedError(err) &&
        !(err instanceof Error && isTransportConnectionErrorMessage(err.message))
      ) {
        setComposerError(threadIdForSend, t("common/failedToSendMessage_u6zrk1"));
      }
    });
    sendInFlightRef.current = false;
    if (!turnStartSucceeded) {
      resetLocalDispatch();
    }
  };

  const onInterrupt = async () => {
    const uploadAbortController = uploadAbortControllerRef.current;
    if (uploadAbortController) {
      uploadAbortControllerRef.current = null;
      uploadAbortController.abort();
      return;
    }
    const api = readEnvironmentApi(environmentId);
    if (!api || !activeThread) return;
    await api.orchestration.dispatchCommand({
      type: "thread.turn.interrupt",
      commandId: newCommandId(),
      threadId: activeThread.id,
    });
  };

  const onExpandTimelineImage = useCallback((preview: ExpandedImagePreview) => {
    setExpandedImage(preview);
  }, []);

  // Empty state: no active thread
  if (!activeThread) {
    return <NoActiveThreadState />;
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
      {/* Top bar */}
      <header
        className={cn(
          "border-b border-border",
          isElectron
            ? cn(
                "drag-region flex h-[52px] items-center px-3 sm:px-5 wco:h-[env(titlebar-area-height)]",
                reserveTitleBarControlInset &&
                  "wco:pr-[calc(100vw-env(titlebar-area-width)-env(titlebar-area-x)+1em)]",
              )
            : "pb-2 pl-[calc(env(safe-area-inset-left)+0.75rem)] pr-[calc(env(safe-area-inset-right)+0.75rem)] pt-2 sm:pb-3 sm:pl-[calc(env(safe-area-inset-left)+1.25rem)] sm:pr-[calc(env(safe-area-inset-right)+1.25rem)] sm:pt-3",
        )}
      >
        <ChatHeader
          activeThreadEnvironmentId={activeThread.environmentId}
          activeThreadId={activeThread.id}
          activeThreadTitle={activeThread.title}
          activeProjectName={activeProject?.name}
          activeProjectOwner={activeProjectOwner}
          draftProjectPicker={draftProjectPicker}
          projectId={activeProject?.id ?? null}
          projectCwd={projectCwd}
          hasMessages={activeThread.messages.length > 0}
          keybindings={keybindings}
        />
      </header>

      {/* Main content area with optional plan sidebar */}
      <div className="flex min-h-0 min-w-0 flex-1">
        {/* Chat column */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Messages Wrapper */}
          <div
            ref={messagesWrapperRef}
            className={cn("relative flex min-h-0 flex-1 flex-col", railVisible && "md:pl-10")}
          >
            <MessagesTimeline
              key={activeThread.id}
              isWorking={isWorking}
              workingLabel={uploadProgress ? formatUploadProgressLabel(uploadProgress) : null}
              activeTurnInProgress={!latestTurnSettled}
              activeTurnId={activeLatestTurn?.turnId ?? null}
              activeTurnStartedAt={activeWorkStartedAt}
              timelineRef={timelineRef}
              timelineEntries={timelineEntries}
              activeThreadEnvironmentId={activeThread.environmentId}
              projectId={activeProject?.id}
              timelineThreadId={activeThread.id}
              routeThreadKey={routeThreadKey}
              onImageExpand={onExpandTimelineImage}
              markdownCwd={projectCwd ?? undefined}
              resolvedTheme={resolvedTheme}
              workspaceRoot={activeWorkspaceRoot}
              skills={agentStatus?.skills ?? EMPTY_AGENT_SKILLS}
              onIsAtEndChange={onIsAtEndChange}
              hasMoreBefore={activeThreadMessageWindow?.hasMoreBefore ?? false}
              loadingOlder={activeThreadMessageWindow?.loadingOlder ?? false}
              onLoadOlder={handleLoadOlderMessages}
              emptyState={<PromptExamples onPick={onPickPromptExample} />}
              loading={isThreadDetailLoading}
            />

            {/* scroll to bottom pill — shown when user has scrolled away from the bottom */}
            {showScrollToBottom && <TimelineScrollToBottomPill onClick={() => scrollToEnd(true)} />}
            {railVisible ? (
              <UserMessageRail
                entries={railEntries}
                scrollerRootRef={messagesWrapperRef}
                threadKey={activeThread.id}
                onSelect={onJumpToMessage}
              />
            ) : null}
          </div>

          {/* Input bar */}
          <div
            className={cn(
              "pl-[calc(env(safe-area-inset-left)+0.75rem)] pr-[calc(env(safe-area-inset-right)+0.75rem)] pt-1.5 sm:pl-[calc(env(safe-area-inset-left)+1.25rem)] sm:pr-[calc(env(safe-area-inset-right)+1.25rem)] sm:pt-2",
              "pb-[calc(env(safe-area-inset-bottom)+0.75rem)] sm:pb-[calc(env(safe-area-inset-bottom)+1rem)]",
            )}
          >
            <div className="relative isolate">
              <div className="relative z-10">
                {composerErrors[`${environmentId}:${activeThread.id}`] ? (
                  <p role="alert" className="mx-auto mb-2 max-w-208 px-1 text-xs text-destructive">
                    {composerErrors[`${environmentId}:${activeThread.id}`]}
                  </p>
                ) : null}
                <ChatComposer
                  composerRef={composerRef}
                  composerDraftTarget={composerDraftTarget}
                  environmentId={environmentId}
                  draftId={draftId}
                  activeThreadId={activeThreadId}
                  activeThreadEnvironmentId={activeThread?.environmentId}
                  isServerThread={isServerThread}
                  isLocalDraftThread={isLocalDraftThread}
                  phase={phase}
                  isConnecting={isConnecting}
                  isSendBusy={isSendBusy || isAcquiringCredential}
                  isUploading={uploadProgress !== null}
                  environmentUnavailable={activeEnvironmentUnavailableState}
                  activePlan={activePlan as { turnId?: TurnId } | null}
                  planSidebarOpen={planSidebarOpen}
                  agentStatus={agentStatus}
                  activeThreadActivities={activeThread?.activities}
                  resolvedTheme={resolvedTheme}
                  projectId={activeProject?.id ?? null}
                  promptRef={promptRef}
                  composerImagesRef={composerImagesRef}
                  composerFileAttachmentsRef={composerFileAttachmentsRef}
                  shouldAutoScrollRef={isAtEndRef}
                  scheduleStickToBottom={scrollToEnd}
                  onSend={onSend}
                  onInterrupt={onInterrupt}
                  togglePlanSidebar={togglePlanSidebar}
                  focusComposer={focusComposer}
                  setComposerError={setComposerError}
                  onExpandImage={onExpandTimelineImage}
                />
              </div>
            </div>
          </div>
        </div>
        {/* end chat column */}

        {/* Plan sidebar */}
        {planSidebarOpen && !shouldUsePlanSidebarSheet ? (
          <PlanSidebar
            activePlan={activePlan}
            timestampFormat={timestampFormat}
            mode="sidebar"
            onClose={closePlanSidebar}
          />
        ) : null}
      </div>
      {/* end horizontal flex container */}

      {shouldUsePlanSidebarSheet ? (
        <RightPanelSheet open={planSidebarOpen} onClose={closePlanSidebar}>
          <PlanSidebar
            activePlan={activePlan}
            timestampFormat={timestampFormat}
            mode="sheet"
            onClose={closePlanSidebar}
          />
        </RightPanelSheet>
      ) : null}

      {expandedImage && (
        <ExpandedImageDialog preview={expandedImage} onClose={closeExpandedImage} />
      )}
    </div>
  );
}
